import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faSearch,
  faFilter,
  faTags,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import ReportsSidebar from './ReportsSidebar';
import ReportsContent from './ReportsContent';

const ReportsLayout = () => {
  const { category, reportName } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [reportsData, setReportsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);

  // Load reports data on mount
  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/reports/discovery', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReportsData(data.reports_by_category || {});
        
        // Extract all unique tags
        const tags = new Set();
        Object.values(data.reports_by_category || {}).forEach(reports => {
          reports.forEach(report => {
            if (report.tags && Array.isArray(report.tags)) {
              report.tags.forEach(tag => tags.add(tag));
            }
          });
        });
        setAllTags(Array.from(tags));
      } else {
        setError('Failed to load reports');
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  // Filter reports based on search and tags
  const getFilteredReports = () => {
    if (!reportsData) return {};

    const filtered = {};
    Object.entries(reportsData).forEach(([cat, reports]) => {
      filtered[cat] = reports.filter(report => {
        // Search filter
        const matchesSearch = !searchTerm || 
          report.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.name.toLowerCase().includes(searchTerm.toLowerCase());

        // Tags filter
        const matchesTags = selectedTags.length === 0 || 
          (report.tags && report.tags.some(tag => selectedTags.includes(tag)));

        return matchesSearch && matchesTags;
      });
    });

    return filtered;
  };

  const filteredReports = getFilteredReports();
  const totalReports = Object.values(filteredReports).reduce((sum, reports) => sum + reports.length, 0);

  if (loading) {
    return (
      <MainLayout title="Reports">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading reports...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout title="Reports">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadReportsData}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Reports">
      <div className="flex h-full">
        {/* Sidebar */}
        <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faChartBar} className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Reports</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {totalReports} reports available
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-3 text-gray-400 text-sm" 
              />
            </div>

            {/* Tags Filter */}
            {allTags.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FontAwesomeIcon icon={faTags} className="text-gray-400 text-sm" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by tags</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => {
                        setSelectedTags(prev => 
                          prev.includes(tag) 
                            ? prev.filter(t => t !== tag)
                            : [...prev, tag]
                        );
                      }}
                      className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                        selectedTags.includes(tag)
                          ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="mt-2 text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar content */}
          <ReportsSidebar
            reportsData={filteredReports}
            selectedCategory={category}
            selectedReport={reportName}
            onCategorySelect={(cat) => navigate(`/reports/${cat}`)}
            onReportSelect={(cat, report) => navigate(`/reports/${cat}/${report}`)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <ReportsContent
            category={category}
            reportName={reportName}
            reportsData={filteredReports}
            token={token}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default ReportsLayout;